import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import toast from 'react-hot-toast'
import {
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  PrinterIcon,
  KeyIcon,
  ClipboardDocumentIcon,
  QrCodeIcon,
} from '@heroicons/react/24/outline'
import { PrinterSettingsFields, emptyForm } from '../../components/printer/PrinterSettingsFields'

const STATUS_FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
]

/** Matches backend `mobileDeviceRequiresPlatformApproval` when MOBILE_DEVICE_REQUIRE_APPROVAL is on. */
function needsExplicitPlatformApproval(d) {
  const st = String(d.approval_status || 'approved').toLowerCase()
  if (st === 'rejected') return false
  if (st === 'pending') return true
  if (st !== 'approved') return false
  const hasAt = d.approved_at != null && String(d.approved_at).trim() !== ''
  const hasBy = d.approved_by != null && String(d.approved_by).trim() !== ''
  return !(hasAt || hasBy)
}

/** Latest event on the row for list ordering (newest first). */
function rowRecencyMs(d) {
  let m = 0
  for (const key of ['last_bootstrap_at', 'approved_at', 'created_at']) {
    const v = d[key]
    if (v == null || v === '') continue
    const t = new Date(v).getTime()
    if (!Number.isNaN(t) && t > m) m = t
  }
  return m
}

/** Human-readable local timestamp for audit (API returns ISO strings from PostgreSQL). */
function formatDeviceTimestamp(v) {
  if (v == null || v === '') return '—'
  const d = v instanceof Date ? v : new Date(v)
  if (Number.isNaN(d.getTime())) return String(v)
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  })
}

/** Stacked date / time + TZ so columns stay narrow and readable. */
function DeviceTimestampCell({ value }) {
  if (value == null || value === '') return <span className="text-gray-400">—</span>
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return <span className="break-all">{String(value)}</span>
  const dateLine = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
  const timeLine = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  const tz =
    Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(d)
      .find((p) => p.type === 'timeZoneName')?.value || ''
  return (
    <div className="min-w-0 leading-tight">
      <div className="text-[11px] font-medium text-gray-800 tabular-nums whitespace-nowrap">{dateLine}</div>
      <div className="text-[10px] text-gray-500 tabular-nums whitespace-nowrap">
        {timeLine}
        {tz ? ` ${tz}` : ''}
      </div>
    </div>
  )
}

function parseRegistrationMeta(raw) {
  if (raw == null) return null
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw
  if (typeof raw === 'string') {
    try {
      const o = JSON.parse(raw)
      return o && typeof o === 'object' && !Array.isArray(o) ? o : null
    } catch {
      return null
    }
  }
  return null
}

/** Primary line = friendly device name from registration metadata (phone/tablet app or gateway PC); secondary = model when both differ. */
function registrationDeviceLines(metaObj) {
  const m = metaObj && typeof metaObj === 'object' ? metaObj : {}
  const name = String(m.device_name || '').trim()
  const model = String(m.device_model || '').trim()
  const primary = name || model || null
  const secondary =
    name && model && name.localeCompare(model, undefined, { sensitivity: 'accent' }) !== 0 ? model : null
  return { primary, secondary }
}

/** True when device should show PSG Gateway encryption key UI (matches backend PSG_GATEWAY_DEVICE_TYPE, default PSGGateway). */
function isPsgGatewayDeviceRow(d) {
  if (d.is_psg_gateway_device === true) return true
  if (d.is_psg_gateway_device === false) return false
  return String(d.device_type || '').trim().toLowerCase() === 'psggateway'
}

const PSG_GATEWAY_APPROVALS_TITLE = 'PSG Gateway'

/** List/detail title: gateway installs often omit device_name in metadata — show a fixed label instead of "Unknown device". */
function deviceApprovalDisplayPrimary(device, metaObj) {
  const { primary } = registrationDeviceLines(metaObj)
  if (primary) return primary
  if (isPsgGatewayDeviceRow(device)) return PSG_GATEWAY_APPROVALS_TITLE
  return 'Unknown device'
}

/** Detail subtitle under title when metadata has no model line — show service username (e.g. psg_gateway). */
function deviceApprovalDetailSecondary(device, metaObj) {
  const { secondary } = registrationDeviceLines(metaObj)
  if (secondary) return secondary
  if (isPsgGatewayDeviceRow(device)) {
    const u = device.registration_username != null ? String(device.registration_username).trim() : ''
    return u || null
  }
  return null
}

export default function MobileDeviceApprovals({ fixedGroupId = '', embedded = false }) {
  const [tenants, setTenants] = useState([])
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(false)
  const [tenantsLoading, setTenantsLoading] = useState(!embedded)
  const [selectedDevice, setSelectedDevice] = useState(null)
  const [rejectOpen, setRejectOpen] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [actionKey, setActionKey] = useState(null)
  const [requireDeviceApproval, setRequireDeviceApproval] = useState(true)
  const [listLoadedAt, setListLoadedAt] = useState(null)
  const [status, setStatus] = useState('all')
  const [groupId, setGroupId] = useState(fixedGroupId || '')

  useEffect(() => {
    if (fixedGroupId) setGroupId(fixedGroupId)
  }, [fixedGroupId])

  const loadTenants = useCallback(async () => {
    if (embedded) return
    setTenantsLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/admin/tenants'))
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setTenants(Array.isArray(data.tenants) ? data.tenants : [])
    } catch (e) {
      toast.error(e.message || 'Failed to load tenants')
      setTenants([])
    } finally {
      setTenantsLoading(false)
    }
  }, [embedded])

  useEffect(() => {
    loadTenants()
  }, [loadTenants])

  const loadAllDevices = useCallback(async (statusOverride) => {
    const effectiveStatus = statusOverride != null ? statusOverride : status
    setLoading(true)
    try {
      const q = `/api/admin/mobile-devices/all?status=${encodeURIComponent(effectiveStatus)}`
      const res = await authFetch(getLocalApiUrl(q))
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`)
      }
      const list = Array.isArray(data.data) ? data.data : []
      setDevices(list)
      setListLoadedAt(new Date())
      if (typeof data.require_device_approval === 'boolean') {
        setRequireDeviceApproval(data.require_device_approval)
      }
      return list
    } catch (e) {
      toast.error(e.message || 'Failed to load devices')
      setDevices([])
      setListLoadedAt(null)
      return []
    } finally {
      setLoading(false)
    }
  }, [status])

  useEffect(() => {
    loadAllDevices()
  }, [loadAllDevices])

  const refreshDevicesKeepSelection = useCallback(async () => {
    const list = await loadAllDevices()
    setSelectedDevice((prev) => {
      if (!prev) return null
      const next = list.find((d) => d.device_id === prev.device_id && d.group_id === prev.group_id)
      return next || prev
    })
  }, [loadAllDevices])

  const sortedDevices = useMemo(() => {
    let filtered = [...devices]
    
    // Filter by tenant if groupId is specified
    if (groupId.trim()) {
      filtered = filtered.filter(d => d.group_id === groupId.trim())
    }
    
    // Sort by created_at desc
    return filtered.sort((a, b) => {
      const ca = a.created_at ? new Date(a.created_at).getTime() : NaN
      const cb = b.created_at ? new Date(b.created_at).getTime() : NaN
      const hasA = !Number.isNaN(ca)
      const hasB = !Number.isNaN(cb)
      if (hasA && hasB && cb !== ca) return cb - ca
      if (hasB && !hasA) return 1
      if (hasA && !hasB) return -1
      return String(b.device_id || '').localeCompare(String(a.device_id || ''))
    })
  }, [devices, groupId])

  // Group devices by date
  const groupedDevices = useMemo(() => {
    const groups = {}
    sortedDevices.forEach(d => {
      const date = d.created_at ? new Date(d.created_at).toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }) : 'Unknown Date'
      if (!groups[date]) {
        groups[date] = []
      }
      groups[date].push(d)
    })
    return groups
  }, [sortedDevices])

  const runAction = async (device, method, endpoint, body) => {
    const key = `${method}:${device.device_id}`
    setActionKey(key)
    try {
      const path = `/api/admin/tenants/${encodeURIComponent(device.group_id)}/mobile-devices/${encodeURIComponent(device.device_id)}${endpoint}`
      const res = await authFetch(getLocalApiUrl(path), {
        method,
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      let reloadStatusOverride
      if (endpoint.includes('/approve')) {
        toast.success('Device approved — switched list to Approved')
        setStatus('approved')
        reloadStatusOverride = 'approved'
      } else if (endpoint.includes('/reject')) {
        toast.success('Device rejected — switched list to Rejected')
        setStatus('rejected')
        reloadStatusOverride = 'rejected'
      } else {
        toast.success('Done')
      }
      await loadAllDevices(reloadStatusOverride)
      setSelectedDevice(null)
    } catch (e) {
      toast.error(e.message || 'Action failed')
    } finally {
      setActionKey(null)
    }
  }

  const submitReject = async () => {
    if (!rejectOpen) return
    await runAction(rejectOpen, 'POST', '/reject', { reason: rejectReason })
    setRejectOpen(null)
    setRejectReason('')
  }

  return (
    <div className={embedded ? 'h-[min(70vh,720px)] flex flex-col bg-gray-50' : 'h-screen flex flex-col bg-gray-50'}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          {!embedded && (
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-psg-teal to-psg-teal/80 flex items-center justify-center">
                <DevicePhoneMobileIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Device queue (all tenants)</h1>
                <p className="text-xs text-gray-500">
                  Floor apps and gateway PCs — pending approvals across every tenant
                </p>
              </div>
            </div>
          )}
          {embedded && (
            <p className="text-sm text-gray-700">
              Devices for <span className="font-mono font-semibold">{fixedGroupId}</span>
            </p>
          )}
          <div className="flex items-center gap-3 ml-auto">
            {!embedded && (
            <div className="min-w-[200px]">
              <input
                type="text"
                list="mobile-device-tenant-gsids"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
                placeholder={tenantsLoading ? 'Loading...' : 'Filter by tenant (optional)'}
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                disabled={tenantsLoading}
              />
              <datalist id="mobile-device-tenant-gsids">
                {tenants.map((t) => (
                  <option key={t.group_id} value={t.group_id} />
                ))}
              </datalist>
            </div>
            )}
            <select
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={loadAllDevices}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg bg-psg-teal px-4 py-2 text-sm font-medium text-white hover:bg-psg-teal/90 disabled:opacity-50 transition-colors"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
        {listLoadedAt && (
          <div className="mt-2 text-xs text-gray-500">
            {sortedDevices.length} device{sortedDevices.length === 1 ? '' : 's'}
            {groupId.trim() && ` in ${groupId}`} · Updated {formatDeviceTimestamp(listLoadedAt)}
          </div>
        )}
      </div>

      {/* Main Content: List + Detail Panel */}
      <div className="flex-1 flex overflow-hidden">
        {/* Device List - Compact */}
        <div className="w-96 border-r border-gray-200 bg-white overflow-y-auto">
          {loading && devices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <ArrowPathIcon className="h-12 w-12 animate-spin mb-3" />
              <p className="text-sm">Loading devices...</p>
            </div>
          ) : devices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 px-6 text-center">
              <DevicePhoneMobileIcon className="h-16 w-16 mb-4" />
              <p className="text-sm font-medium text-gray-600">No devices found</p>
              <p className="text-xs mt-1">No {status === 'all' ? '' : status} devices at this time</p>
            </div>
          ) : sortedDevices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 px-6 text-center">
              <DevicePhoneMobileIcon className="h-16 w-16 mb-4" />
              <p className="text-sm font-medium text-gray-600">No matching devices</p>
              <p className="text-xs mt-1">Try a different tenant or status filter</p>
            </div>
          ) : (
            <div>
              {Object.entries(groupedDevices).map(([date, devicesInGroup]) => (
                <div key={date}>
                  {/* Date Header */}
                  <div className="sticky top-0 bg-gray-100 px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 z-10">
                    {date} ({devicesInGroup.length})
                  </div>
                  {/* Devices in this date group */}
                  <div className="divide-y divide-gray-100">
                    {devicesInGroup.map((d) => {
                      const metaObj = parseRegistrationMeta(d.registration_metadata)
                      const st = String(d.approval_status || '').toLowerCase()
                      const needsApproval =
                        typeof d.awaiting_platform_action === 'boolean'
                          ? d.awaiting_platform_action
                          : requireDeviceApproval && needsExplicitPlatformApproval(d)
                      const isSelected = selectedDevice?.device_id === d.device_id
                      const registeredTime = d.created_at ? new Date(d.created_at).toLocaleTimeString(undefined, {
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : ''
                      
                      return (
                        <button
                          key={d.device_id}
                          onClick={() => setSelectedDevice(d)}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                            isSelected ? 'bg-psg-teal/5 border-l-4 border-psg-teal' : 'border-l-4 border-transparent'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-0.5">
                              <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                                st === 'approved' && !needsApproval
                                  ? 'bg-emerald-50 text-emerald-600'
                                  : st === 'rejected'
                                    ? 'bg-red-50 text-red-600'
                                    : 'bg-amber-50 text-amber-600'
                              }`}>
                                <DevicePhoneMobileIcon className="h-4 w-4" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="font-medium text-sm text-gray-900 truncate">
                                  {deviceApprovalDisplayPrimary(d, metaObj)}
                                </div>
                                {registeredTime && (
                                  <div className="flex-shrink-0 text-xs font-medium text-gray-500">
                                    {registeredTime}
                                  </div>
                                )}
                              </div>
                              <div className="text-xs text-gray-600 mt-0.5">{d.registration_username || '—'}</div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] font-mono text-gray-500">{d.group_id}</span>
                                <span className="text-[10px] text-gray-400">•</span>
                                <span className="text-[10px] text-gray-500">{d.tenant_name || d.group_id}</span>
                              </div>
                              {needsApproval && (
                                <div className="mt-1.5">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800">
                                    Needs Approval
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          {!selectedDevice ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              <div className="text-center">
                <DevicePhoneMobileIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-sm font-medium text-gray-600">Select a device</p>
                <p className="text-xs mt-1">Choose a device from the list to view details</p>
              </div>
            </div>
          ) : (
            <DeviceDetailPanel
              device={selectedDevice}
              onClose={() => setSelectedDevice(null)}
              onApprove={(d) => runAction(d, 'POST', '/approve')}
              onReject={(d) => {
                setRejectOpen(d)
                setRejectReason('')
              }}
              onDelete={(d) => {
                if (window.confirm(`Remove device ${d.device_id} from tenant ${d.group_id}?`)) {
                  runAction(d, 'DELETE', '')
                }
              }}
              onRefreshDevices={refreshDevicesKeepSelection}
              actionKey={actionKey}
              requireDeviceApproval={requireDeviceApproval}
            />
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {rejectOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Reject device</h2>
            <p className="text-sm text-gray-600 font-mono break-all">{rejectOpen.device_id}</p>
            <label className="block text-xs font-medium text-gray-500">Reason (optional)</label>
            <textarea
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm min-h-[80px]"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Shown to the client on bootstrap when status is rejected"
            />
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => {
                  setRejectOpen(null)
                  setRejectReason('')
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                disabled={actionKey != null}
                onClick={submitReject}
              >
                Reject device
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

/** NSW DDL: per-tablet QR image + Service NSW trigger identifiers. Trigger name must match webhook `trigger.triggerName`. */
function DeviceDdrTriggerSection({ device, onRefreshDevices }) {
  const gid = device.group_id
  const did = device.device_id
  const [value, setValue] = useState(() =>
    device.ddr_trigger_name != null && String(device.ddr_trigger_name).trim() !== ''
      ? String(device.ddr_trigger_name).trim()
      : ''
  )
  const [triggerId, setTriggerId] = useState(() =>
    device.ddr_trigger_id != null && String(device.ddr_trigger_id).trim() !== ''
      ? String(device.ddr_trigger_id).trim()
      : ''
  )
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [previewSignedUrl, setPreviewSignedUrl] = useState(null)

  useEffect(() => {
    setValue(
      device.ddr_trigger_name != null && String(device.ddr_trigger_name).trim() !== ''
        ? String(device.ddr_trigger_name).trim()
        : ''
    )
    setTriggerId(
      device.ddr_trigger_id != null && String(device.ddr_trigger_id).trim() !== ''
        ? String(device.ddr_trigger_id).trim()
        : ''
    )
  }, [device.device_id, device.group_id, device.ddr_trigger_name, device.ddr_trigger_id])

  useEffect(() => {
    let cancelled = false
    const raw =
      device.ddr_trigger_image_url != null && String(device.ddr_trigger_image_url).trim() !== ''
        ? String(device.ddr_trigger_image_url).trim()
        : ''
    if (!raw) {
      setPreviewSignedUrl(null)
      return undefined
    }
    ;(async () => {
      try {
        const res = await authFetch(
          getLocalApiUrl(
            `/api/admin/tenants/${encodeURIComponent(gid)}/mobile-devices/${encodeURIComponent(did)}/nsw-ddl-qr-signed-url`
          )
        )
        const j = await res.json().catch(() => ({}))
        if (!cancelled && res.ok && j.success && j.data?.url) setPreviewSignedUrl(j.data.url)
        else if (!cancelled) setPreviewSignedUrl(null)
      } catch {
        if (!cancelled) setPreviewSignedUrl(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [gid, did, device.ddr_trigger_image_url])

  const save = async () => {
    setSaving(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(
          `/api/admin/tenants/${encodeURIComponent(gid)}/mobile-devices/${encodeURIComponent(did)}`
        ),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ddr_trigger_name: value.trim(),
            ddr_trigger_id: triggerId.trim(),
          }),
        }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || data.code || `HTTP ${res.status}`)
      toast.success('DDR trigger settings saved')
      if (onRefreshDevices) await onRefreshDevices()
    } catch (e) {
      toast.error(e.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const removeQr = async () => {
    if (!window.confirm('Remove the NSW Digital Licence QR image from this tablet?')) return
    setSaving(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(
          `/api/admin/tenants/${encodeURIComponent(gid)}/mobile-devices/${encodeURIComponent(did)}`
        ),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ddr_trigger_image_url: '' }),
        }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || data.code || `HTTP ${res.status}`)
      toast.success('QR image removed')
      setPreviewSignedUrl(null)
      if (onRefreshDevices) await onRefreshDevices()
    } catch (e) {
      toast.error(e.message || 'Remove failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <QrCodeIcon className="h-4 w-4 text-psg-teal" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">NSW DDL — DDR trigger & tablet QR</h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Set when Service NSW allocates identifiers for this terminal. <strong>DDRTriggerName</strong> must match{' '}
        <code className="text-[10px] bg-gray-100 px-1 rounded">trigger.triggerName</code> on{' '}
        <code className="text-[10px] bg-gray-100 px-1 rounded">POST /api/mobile/webhooks/nsw-ddl</code> (webhook routing).
        Optional <strong>DDRTriggerId</strong> is stored as <code className="text-[10px] bg-gray-100 px-1 rounded">ddr_trigger_id</code> and returned to the mobile app as{' '}
        <code className="text-[10px] bg-gray-100 px-1 rounded">ddr_trigger.id</code> on bootstrap / login.{' '}
        <strong>NSW Digital Licence QR</strong> is stored per tablet (not venue payout config); PaySafeGo loads it from{' '}
        <code className="text-[10px] bg-gray-100 px-1 rounded">GET /api/mobile/payout-config</code> when NSW is enabled.
      </p>

      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-3 mb-4">
        <div className="text-xs font-medium text-gray-700 mb-2">NSW Digital Licence QR (this tablet)</div>
        <p className="text-[11px] text-gray-500 mb-2">
          Upload the QR image patrons scan in Service NSW for this device. Requires tenant Azure blob storage (same as other uploads).
        </p>
        {previewSignedUrl ? (
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <img src={previewSignedUrl} alt="NSW QR preview" className="w-24 h-24 border rounded object-contain bg-white" />
            <button
              type="button"
              onClick={removeQr}
              disabled={saving}
              className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 disabled:opacity-50"
            >
              <TrashIcon className="w-3.5 h-3.5" /> Remove image
            </button>
          </div>
        ) : device.ddr_trigger_image_url ? (
          <p className="text-xs text-amber-700 mb-2">Could not load preview (check blob access). Image URL is still stored.</p>
        ) : (
          <p className="text-xs text-gray-500 mb-2">No QR image uploaded yet.</p>
        )}
        <input
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          disabled={uploading || saving}
          className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-psg-teal file:text-white"
          onChange={async (e) => {
            const file = e.target.files && e.target.files[0]
            e.target.value = ''
            if (!file) return
            setUploading(true)
            try {
              const fd = new FormData()
              fd.append('file', file)
              const res = await authFetch(
                getLocalApiUrl(
                  `/api/admin/tenants/${encodeURIComponent(gid)}/mobile-devices/${encodeURIComponent(did)}/nsw-ddl-qr`
                ),
                { method: 'POST', body: fd }
              )
              const j = await res.json().catch(() => ({}))
              if (!res.ok) throw new Error(j.error || j.code || `HTTP ${res.status}`)
              toast.success('QR image uploaded')
              if (onRefreshDevices) await onRefreshDevices()
            } catch (err) {
              toast.error(err.message || 'Upload failed')
            } finally {
              setUploading(false)
            }
          }}
        />
        {uploading ? <span className="text-xs text-gray-500 ml-2">Uploading…</span> : null}
      </div>

      <label className="block text-xs font-medium text-gray-600 mb-1">Trigger name (max 128 characters)</label>
      <input
        type="text"
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono mb-3"
        placeholder="e.g. RXVENUEVM1"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={128}
        spellCheck={false}
      />
      <label className="block text-xs font-medium text-gray-600 mb-1">Trigger ID (max 32 characters, optional)</label>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
          placeholder="e.g. from Service NSW portal"
          value={triggerId}
          onChange={(e) => setTriggerId(e.target.value)}
          maxLength={32}
          spellCheck={false}
        />
        <button
          type="button"
          disabled={saving}
          onClick={save}
          className="rounded-lg bg-psg-teal px-4 py-2 text-sm font-medium text-white hover:bg-psg-teal/90 disabled:opacity-50 shrink-0"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}

function DeviceGatewayPayloadKeySection({ device, onRefreshDevices }) {
  const gid = device.group_id
  const did = device.device_id
  const hasKey = !!device.has_gateway_payload_key
  const [busy, setBusy] = useState(false)
  const [reveal, setReveal] = useState(null)

  const rotate = async () => {
    const msg = hasKey
      ? 'Replace the encryption key? Update the Windows gateway service with the new key or decryption will fail.'
      : 'Generate an encryption key for this gateway? You will copy it into the gateway service configuration.'
    if (!window.confirm(msg)) return
    setBusy(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(
          `/api/admin/tenants/${encodeURIComponent(gid)}/mobile-devices/${encodeURIComponent(did)}/gateway-payload-key`
        ),
        { method: 'POST' }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || data.code || `HTTP ${res.status}`)
      const payload = data.data || {}
      setReveal({
        base64: payload.gateway_payload_key_base64,
        rotated_at: payload.gateway_payload_key_rotated_at,
      })
      toast.success('Encryption key created — copy it now; it will not be shown again.')
      if (onRefreshDevices) await onRefreshDevices()
    } catch (e) {
      toast.error(e.message || 'Failed to generate key')
    } finally {
      setBusy(false)
    }
  }

  const copyKey = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success('Copied')
    } catch {
      toast.error('Copy failed')
    }
  }

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <KeyIcon className="h-4 w-4 text-psg-teal" />
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Gateway payload encryption</h3>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Used for <span className="font-mono">POST /api/mobile/psg/gateway-configurations</span> (AES-256-GCM). Integrator reference:{' '}
          <code className="text-[10px] bg-gray-100 px-1 rounded">docs/psg-gateway-payload-encryption.md</code>
        </p>
        <dl className="grid grid-cols-2 gap-3 mb-4 text-sm">
          <div>
            <dt className="text-xs text-gray-500">Key on server</dt>
            <dd className="mt-1">{hasKey ? 'Configured' : 'Not set'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Last rotated</dt>
            <dd className="mt-1">
              {device.gateway_payload_key_rotated_at ? formatDeviceTimestamp(device.gateway_payload_key_rotated_at) : '—'}
            </dd>
          </div>
        </dl>
        <button
          type="button"
          disabled={busy}
          onClick={rotate}
          className="inline-flex items-center gap-2 rounded-lg bg-psg-teal px-4 py-2 text-sm font-medium text-white hover:bg-psg-teal/90 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
          {hasKey ? 'Rotate encryption key' : 'Generate encryption key'}
        </button>
      </div>

      {reveal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Gateway encryption key</h2>
            <p className="text-sm text-gray-600">
              Copy this base64 key into the gateway service. It is not kept in plaintext and cannot be retrieved later.
            </p>
            {reveal.rotated_at ? (
              <p className="text-xs text-gray-500">Rotated at {formatDeviceTimestamp(reveal.rotated_at)}</p>
            ) : null}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-xs break-all max-h-40 overflow-y-auto">
              {reveal.base64}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => copyKey(reveal.base64)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                <ClipboardDocumentIcon className="h-4 w-4" />
                Copy key
              </button>
              <button
                type="button"
                onClick={() => setReveal(null)}
                className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

function DevicePrinterSection({ device }) {
  const gid = device.group_id
  const did = device.device_id
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [printerRow, setPrinterRow] = useState(null)
  const [form, setForm] = useState(() => emptyForm())
  const [independentTemplates, setIndependentTemplates] = useState([])
  const [templateChoice, setTemplateChoice] = useState('')

  const rowToForm = (row) => ({
    label: row.label || '',
    enable_printer: !!row.enable_printer,
    printer_default_gateway: row.printer_default_gateway || '',
    printer_ip_address: row.printer_ip_address || '',
    printer_ip_subnet_mask: row.printer_ip_subnet_mask || '',
    printer_model_number: row.printer_model_number || '',
    printer_port_number: row.printer_port_number || '',
    enable_payment: !!row.enable_payment,
    printer_brand: row.printer_brand || 'STAR',
    printer_model_kind: row.printer_model_kind || 'bt_portable',
    gsl_type: row.gsl_type || 'api',
    enable_gsl_receipt: !!row.enable_gsl_receipt,
    sort_order: row.sort_order ?? 0,
  })

  const loadIndependents = useCallback(async () => {
    try {
      const res = await authFetch(getLocalApiUrl(`/api/admin/tenants/${encodeURIComponent(gid)}/printers`))
      const data = await res.json().catch(() => ({}))
      if (!res.ok) return
      const rows = Array.isArray(data.data) ? data.data : []
      setIndependentTemplates(rows.filter((p) => !p.mobile_device_id))
    } catch {
      setIndependentTemplates([])
    }
  }, [gid])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(
          `/api/admin/tenants/${encodeURIComponent(gid)}/printers?device_id=${encodeURIComponent(did)}`
        )
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      const rows = Array.isArray(data.data) ? data.data : []
      const row = rows[0] || null
      setPrinterRow(row)
      setForm(row ? rowToForm(row) : emptyForm())
      setTemplateChoice('')
      await loadIndependents()
    } catch (e) {
      toast.error(e.message || 'Failed to load printer')
      setPrinterRow(null)
      setForm(emptyForm())
      setTemplateChoice('')
    } finally {
      setLoading(false)
    }
  }, [gid, did, loadIndependents])

  useEffect(() => {
    load()
  }, [load])

  const patchField = (key, val) => setForm((f) => ({ ...f, [key]: val }))

  const save = async () => {
    setSaving(true)
    try {
      const body = { ...form, mobile_device_id: did }
      if (printerRow?.id) {
        const res = await authFetch(
          getLocalApiUrl(
            `/api/admin/tenants/${encodeURIComponent(gid)}/printers/${encodeURIComponent(printerRow.id)}`
          ),
          { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
        )
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      } else {
        const res = await authFetch(getLocalApiUrl(`/api/admin/tenants/${encodeURIComponent(gid)}/printers`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      }
      toast.success('Printer saved')
      await load()
    } catch (e) {
      toast.error(e.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!printerRow?.id) return
    if (!window.confirm('Remove printer configuration for this device? The tenant default printers (if any) will apply.')) {
      return
    }
    setSaving(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(
          `/api/admin/tenants/${encodeURIComponent(gid)}/printers/${encodeURIComponent(printerRow.id)}`
        ),
        { method: 'DELETE' }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      toast.success('Printer removed')
      await load()
    } catch (e) {
      toast.error(e.message || 'Remove failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <PrinterIcon className="h-4 w-4 text-psg-teal" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Receipt printer (this device)</h3>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        When configured here, this device uses these settings in payout-config. Otherwise the tenant default printers
        apply (see Tenant printers). You can copy field values from an independent printer, or link/move an independent
        row from Tenant printers.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
          <ArrowPathIcon className="h-5 w-5 animate-spin" />
          Loading…
        </div>
      ) : (
        <>
          {!printerRow?.id && (
            <>
              <p className="text-sm text-gray-600 mb-2">No device-specific printer yet — fill in and save to create one.</p>
              {independentTemplates.length > 0 ? (
                <label className="block mb-4">
                  <span className="text-xs font-medium text-gray-600">Start from independent printer (optional)</span>
                  <select
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    value={templateChoice}
                    onChange={(e) => {
                      const v = e.target.value
                      setTemplateChoice(v)
                      if (!v) {
                        setForm(emptyForm())
                        return
                      }
                      const row = independentTemplates.find((p) => p.id === v)
                      if (row) setForm(rowToForm(row))
                    }}
                  >
                    <option value="">— Blank form —</option>
                    {independentTemplates.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label || 'Unnamed'} ({p.group_id})
                      </option>
                    ))}
                  </select>
                  <span className="text-[10px] text-gray-500 mt-1 block">
                    Copies settings into this form; save creates a new device-linked row (independent row unchanged).
                  </span>
                </label>
              ) : null}
            </>
          )}
          <PrinterSettingsFields form={form} onFieldChange={patchField} variant="deviceApproval" />
          <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100">
            {printerRow?.id ? (
              <button
                type="button"
                onClick={remove}
                disabled={saving}
                className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
              >
                <TrashIcon className="h-4 w-4" />
                Remove device printer
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              disabled={saving}
              onClick={save}
              className="rounded-lg bg-psg-teal px-5 py-2 text-sm font-medium text-white hover:bg-psg-teal/90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : printerRow?.id ? 'Save' : 'Create'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function DeviceDetailPanel({
  device,
  onClose,
  onApprove,
  onReject,
  onDelete,
  onRefreshDevices,
  actionKey,
  requireDeviceApproval,
}) {
  const metaObj = parseRegistrationMeta(device.registration_metadata)
  const displayPrimary = deviceApprovalDisplayPrimary(device, metaObj)
  const displaySecondary = deviceApprovalDetailSecondary(device, metaObj)
  const st = String(device.approval_status || '').toLowerCase()
  const needsApproval =
    typeof device.awaiting_platform_action === 'boolean'
      ? device.awaiting_platform_action
      : requireDeviceApproval && needsExplicitPlatformApproval(device)
  
  const statusLabel =
    needsApproval && st === 'pending'
      ? 'pending'
      : needsApproval && st === 'approved'
        ? 'needs approval'
        : needsApproval
          ? 'awaiting action'
          : st || '—'
  
  const clientMetaRows = [
    ['App Version', metaObj?.app_version],
    ['OS Version', metaObj?.os_version],
    ['Device Model', metaObj?.device_model],
    ['Device Name', metaObj?.device_name],
  ].filter(([, v]) => v != null && String(v).trim() !== '')
  
  const metaJson = metaObj != null ? JSON.stringify(metaObj, null, 2) : null

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-psg-teal/10 to-psg-teal/5 flex items-center justify-center border border-psg-teal/20">
              <DevicePhoneMobileIcon className="h-6 w-6 text-psg-teal" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {displayPrimary}
              </h2>
              {displaySecondary && (
                <p className="text-sm text-gray-600 mt-0.5">{displaySecondary}</p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                    st === 'approved' && !needsApproval
                      ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20'
                      : st === 'rejected'
                        ? 'bg-red-50 text-red-700 ring-1 ring-red-600/20'
                        : 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20'
                  }`}
                >
                  {statusLabel}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-4">
          {needsApproval && (
            <button
              type="button"
              onClick={() => onApprove(device)}
              disabled={actionKey != null}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              <CheckCircleIcon className="h-4 w-4" />
              Approve Device
            </button>
          )}
          {st !== 'rejected' && (
            <button
              type="button"
              onClick={() => onReject(device)}
              className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 transition-colors"
            >
              <XCircleIcon className="h-4 w-4" />
              Reject
            </button>
          )}
          <button
            type="button"
            onClick={() => onDelete(device)}
            disabled={actionKey != null}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <TrashIcon className="h-4 w-4" />
            Remove
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Tenant Info */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Tenant Information</h3>
          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt className="text-xs text-gray-500">Tenant GSID</dt>
              <dd className="text-sm font-mono text-gray-900 mt-1">{device.group_id}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Tenant Name</dt>
              <dd className="text-sm text-gray-900 mt-1">{device.tenant_name || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Venue ID</dt>
              <dd className="text-sm font-mono text-gray-900 mt-1">{device.venue_id_at_register || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Username</dt>
              <dd className="text-sm text-gray-900 mt-1">{device.registration_username || '—'}</dd>
            </div>
          </dl>
        </div>

        {/* Device Info */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Device Information</h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-xs text-gray-500">Device type</dt>
              <dd className="text-sm font-mono text-gray-900 mt-1">{device.device_type || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Device ID</dt>
              <dd className="text-sm font-mono text-gray-900 mt-1 break-all">{device.device_id}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Fingerprint</dt>
              <dd className="text-sm font-mono text-gray-900 mt-1 break-all">{device.client_fingerprint || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">APNs device token (hex)</dt>
              <dd className="text-sm font-mono text-gray-900 mt-1 break-all">
                {device.apns_device_token && String(device.apns_device_token).trim() !== ''
                  ? String(device.apns_device_token).trim()
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">APNs token last updated</dt>
              <dd className="text-sm text-gray-900 mt-1">
                {device.apns_token_updated_at ? formatDeviceTimestamp(device.apns_token_updated_at) : '—'}
              </dd>
            </div>
            {clientMetaRows.length > 0 && (
              <>
                {clientMetaRows.map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs text-gray-500">{label}</dt>
                    <dd className="text-sm text-gray-900 mt-1">{String(value)}</dd>
                  </div>
                ))}
              </>
            )}
          </dl>
        </div>

        {isPsgGatewayDeviceRow(device) ? (
          <DeviceGatewayPayloadKeySection device={device} onRefreshDevices={onRefreshDevices} />
        ) : null}

        <DeviceDdrTriggerSection device={device} onRefreshDevices={onRefreshDevices} />

        <DevicePrinterSection device={device} />

        {/* Timestamps */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Timeline</h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-xs text-gray-500">Registered</dt>
              <dd className="text-sm text-gray-900 mt-1">
                {device.created_at ? formatDeviceTimestamp(device.created_at) : '—'}
              </dd>
            </div>
            {device.approved_at && (
              <div>
                <dt className="text-xs text-gray-500">Approved</dt>
                <dd className="text-sm text-gray-900 mt-1">
                  {formatDeviceTimestamp(device.approved_at)}
                  {device.approved_by && (
                    <span className="text-gray-500 ml-2">by {device.approved_by}</span>
                  )}
                </dd>
              </div>
            )}
            {device.last_bootstrap_at && (
              <div>
                <dt className="text-xs text-gray-500">Last Bootstrap</dt>
                <dd className="text-sm text-gray-900 mt-1">
                  {formatDeviceTimestamp(device.last_bootstrap_at)}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Rejection Reason */}
        {device.rejected_reason && (
          <div className="bg-red-50 rounded-lg border border-red-200 p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-red-700 mb-2">Rejection Reason</h3>
            <p className="text-sm text-red-900">{device.rejected_reason}</p>
          </div>
        )}

        {/* Raw Metadata */}
        {metaJson && metaJson !== '{}' && (
          <details className="bg-white rounded-lg border border-gray-200 p-4 group">
            <summary className="text-xs font-semibold uppercase tracking-wider text-gray-500 cursor-pointer hover:text-gray-700 transition-colors list-none flex items-center gap-2">
              <span className="inline-block group-open:rotate-90 transition-transform">▸</span>
              Raw Registration Metadata
            </summary>
            <pre className="mt-3 text-xs text-gray-700 bg-gray-50 rounded border border-gray-200 p-3 overflow-x-auto">
              {metaJson}
            </pre>
          </details>
        )}
      </div>
    </div>
  )
}
